import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid
} from 'recharts';
import { Paper, Typography, Skeleton, Box } from '@mui/material';
import { formatMoney } from '../../../utils/currency';

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  const date = payload[0].payload.date;
  const total = payload.reduce((sum, entry) => sum + entry.value, 0);

  return (
    <div style={{ backgroundColor: '#fff', padding: 10, border: '1px solid #ccc' }}>
      <strong>📅 {date}</strong>
      <div><strong>💰 Total: {formatMoney(total)}</strong></div>
      {payload.map((entry, idx) => (
        <div key={idx} style={{ color: entry.color }}>
          {entry.name}: {formatMoney(entry.value)}
        </div>
      ))}
    </div>
  );
};

const BranchSalesTrendChart = ({ data = [], branches = [], loading }) => {
  // 🔁 Transformar sales: { "20": 540 } → { 20: 540 }
  const transformedData = data.map(entry => ({
    date: entry.date,
    display: entry.display,
    ...entry.sales
  }));

  // ✅ Armar array acumulativo por sucursal
  const branchIds = branches.map((b) => b.id);
  const cumulativeData = [];
  const cumulativeTotals = {};
  branchIds.forEach((id) => (cumulativeTotals[id] = 0));

  for (const day of transformedData) {
    const cumulativeDay = { ...day };
    branchIds.forEach((id) => {
      cumulativeTotals[id] += day[id] ?? 0;
      cumulativeDay[id] = cumulativeTotals[id];
    });
    cumulativeData.push(cumulativeDay);
  }

  return (
    <Paper elevation={2} sx={{ p: 2, height: 360 }}>
      <Typography variant="subtitle1" gutterBottom>
        Ventas acumuladas por sucursal
      </Typography>
      <Box sx={{ width: '100%', height: 300 }}>
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height={300} />
        ) : cumulativeData.length > 0 && branches.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={cumulativeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="display" />
              <YAxis domain={[0, (dataMax) => Math.ceil(dataMax / 5000) * 5000]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {branches.map((branch) => (
                <Line
                  key={branch.id}
                  type="monotone"
                  dataKey={String(branch.id)}
                  name={branch.deleted ? `${branch.name} (eliminada)` : branch.name}
                  stroke={branch.color}
                  strokeDasharray={branch.deleted ? '5 4' : undefined}
                  strokeOpacity={branch.deleted ? 0.6 : 1}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="textSecondary">
              No hay datos disponibles.
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default BranchSalesTrendChart;
